import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArenaCategoryHold, getClanCategoryHolds, pinCategoryPost } from '../../../api/group/arena.api';
import { ClanPostItem, getClanPosts } from '../../../api/posts/clan-posts.api';
import { useCategories } from '../../../hooks/use-categories/useCategories';

interface Props {
  clanId: string;
  memberIds: string[];
}

const BTN = 'rounded-full px-4 py-2 text-[14px] font-medium transition disabled:opacity-60';

// Chuyên mục clan đang giữ tuần này. Chủ và phó clan chọn một bài của thành viên để ghim lên đầu chuyên mục.
const ClanCategoryHolds: React.FC<Props> = ({ clanId, memberIds }) => {
  const [holds, setHolds] = useState<ArenaCategoryHold[]>([]);
  const [pickFor, setPickFor] = useState<string | null>(null);
  const [posts, setPosts] = useState<ClanPostItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const { postCategories } = useCategories({ type: 'post' });

  const load = () =>
    getClanCategoryHolds(clanId)
      .then((h) => {
        setHolds(h || []);
        setLoadFailed(false);
      })
      .catch(() => {
        setHolds([]);
        setLoadFailed(true);
      });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clanId]);

  const openPicker = async (categoryId: string) => {
    setPickFor(categoryId);
    try {
      const res = await getClanPosts(memberIds, 1, 50);
      setPosts(res.posts || []);
    } catch {
      setPosts([]);
    }
  };

  const pin = async (categoryId: string, postId: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await pinCategoryPost(categoryId, postId);
      toast.success('Đã ghim bài lên chuyên mục');
      setPickFor(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Không ghim được bài');
    } finally {
      setBusy(false);
    }
  };

  if (loadFailed) {
    return <p className="text-[14px] text-[#657384]">Chưa tải được chuyên mục đang giữ.</p>;
  }
  if (holds.length === 0) return null;
  const nameOf = (id: string) => (postCategories || []).find((c: any) => c.id === id)?.name || 'Chuyên mục';

  return (
    <section className="flex flex-col gap-3" data-testid="clan-category-holds">
      <h2 className="text-[20px] font-semibold text-[#19052f] dark:text-white">Chuyên mục đang giữ</h2>
      {holds.map((h) => (
        <div key={h.categoryId} className="flex flex-col gap-2 rounded-[16px] bg-white p-4 shadow-[4px_4px_44px_rgba(0,0,0,0.05)] dark:bg-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[15px] font-medium text-[#19052f] dark:text-white">
              {nameOf(h.categoryId)}
              <span className="ml-2 text-[12px] text-[#657384]">
                {h.pinnedPostId ? 'Đã ghim một bài' : 'Chưa ghim bài nào'}
              </span>
            </div>
            <button className={`${BTN} bg-[#066bff] text-white`} onClick={() => openPicker(h.categoryId)}>
              Chọn bài để ghim
            </button>
          </div>
          {pickFor === h.categoryId && (
            <div className="flex max-h-[320px] flex-col divide-y divide-[#F1F5F9] overflow-y-auto rounded-[12px] border border-[#DEE0E6] dark:divide-slate-700 dark:border-slate-600">
              <p className="p-3 text-[12px] text-[#657384]">Chỉ ghim được bài thuộc chuyên mục này.</p>
              {posts.length === 0 && <div className="p-3 text-[13px] text-[#657384]">Thành viên chưa có bài nào để ghim.</div>}
              {posts.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 p-3">
                  <span className="line-clamp-1 text-[14px] text-[#19052f] dark:text-white">
                    {(p.title || p.content || '').trim().slice(0, 80) || 'Bài viết'}
                  </span>
                  <button className={`${BTN} border border-[#066bff] text-[#066bff]`} disabled={busy} onClick={() => pin(h.categoryId, p.id)}>
                    Ghim
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </section>
  );
};

export default ClanCategoryHolds;
